import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cargomatic/ui';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { startCase } from 'lodash';
import { searchCustomers } from 'utils/CustomersAPIUtils';
import { titleize } from 'underscore.string';

export function AddAccessorialCustomersMarketModal({
  showModal,
  onClose,
  customerData,
  onCustomersChange,
  marketsArray,
  onMarketChange,
  market,
  searchCustomerType,
  setShowAddAccessorialsModal,
  disableButtons,
  missingRequiredFields,
  areCustomersNaiveToMarket,
  areCustomersTheSameType,
  isCustomerTypeValid,
  isFetchingExistingCustomers,
  duplicateCustomers,
}) {
  const [saveDisabled, setSaveDisabled] = useState(false);
  const { t } = useTranslation();

  // MultiSelect state
  const [searchInput, setSearchInput] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  // Monotonically-incremented seq on each search; used to discard stale
  // responses so older queries can't overwrite the latest results.
  const searchSeqRef = useRef(0);

  useEffect(() => {
    setSaveDisabled(disableButtons);
  }, [disableButtons]);

  // Search customers when input changes
  const handleSearchInputChange = useCallback(
    async (value) => {
      setSearchInput(value);
      if (!value || value.length <= 2) {
        setCustomerOptions([]);
        return;
      }
      const seq = ++searchSeqRef.current;
      setIsSearching(true);
      try {
        const customerType =
          searchCustomerType !== 'customer' ? searchCustomerType : undefined;
        const results = await searchCustomers({
          queryText: value,
          customerTypes: customerType ? [customerType] : [],
        });
        if (seq !== searchSeqRef.current) return;
        const options = results.map((item) => ({
          value: item._id,
          label: titleize(
            item.optimized_name || item.name || item.company_name,
          ),
          _raw: item,
        }));
        setCustomerOptions(options);
      } catch (_e) {
        if (seq !== searchSeqRef.current) return;
        setCustomerOptions([]);
      } finally {
        if (seq === searchSeqRef.current) setIsSearching(false);
      }
    },
    [searchCustomerType],
  );

  // When customers change in MultiSelect — compute the full desired list and
  // hand it to the parent in one call. Building it incrementally via
  // add/change/remove callbacks created stale-closure races that left behind
  // blank rows (and kept the missing-required-fields alert up after a
  // selection).
  const handleCustomerSelect = (selected) => {
    const existingById = new Map(
      customerData.filter((c) => c.customer).map((c) => [c.customer, c]),
    );

    const nextCustomers = selected
      .map((sel) => {
        const existing = existingById.get(sel.value);
        if (existing) return existing;
        const raw = sel._raw;
        if (!raw) return null;
        return {
          customer: raw._id,
          customerType: (raw._type || raw.type || '').toLowerCase(),
          name: raw.company_name || raw.name,
        };
      })
      .filter(Boolean);

    onCustomersChange(nextCustomers);
    setSearchInput('');
    setCustomerOptions([]);
  };

  const handleAddRates = () => {
    onClose();
    setShowAddAccessorialsModal(true);
  };

  const handleOpenChange = (open) => {
    if (!open) {
      onClose(true);
    }
  };

  const selectedCustomerValues = customerData
    .filter((c) => c.customer)
    .map((c) => ({
      value: c.customer,
      label: c.name || c.customer,
    }));

  return (
    <Dialog open={showModal} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('STEP_1_ENTER_CUSTOMER_AND_MARKET')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 flex-1 min-h-0 overflow-y-auto">
          {isFetchingExistingCustomers && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('LOADING')}
            </div>
          )}

          {/* Market */}
          <div>
            <Label htmlFor="market-select">
              {startCase(t('MARKET', { count: 1 }))}
            </Label>
            <Select
              value={market || ''}
              onValueChange={(value) => {
                const selected = marketsArray.find((m) => m.slug === value);
                if (selected) onMarketChange(selected);
              }}
            >
              <SelectTrigger id="market-select" className="mt-1">
                <SelectValue
                  placeholder={startCase(t('MARKET', { count: 1 }))}
                />
              </SelectTrigger>
              <SelectContent>
                {(marketsArray || []).map((m) => (
                  <SelectItem key={m.slug} value={m.slug}>
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Users - MultiSelect search */}
          <div>
            <Label>{startCase(t('USER', { count: 2 }))}</Label>
            <MultiSelect
              modal
              options={customerOptions}
              value={selectedCustomerValues}
              onChange={handleCustomerSelect}
              useExternalFiltering
              inputValue={searchInput}
              onInputValueChange={handleSearchInputChange}
              filteredOptions={customerOptions}
              isLoading={isSearching}
              placeholder={t('SEARCH_FOR_CUSTOMER')}
              debounceMs={300}
              noOptionsMessage={
                searchInput.length > 2
                  ? t('NO_RESULTS')
                  : t('SEARCH_FOR_CUSTOMER')
              }
            />
          </div>

          {/* Errors */}
          {missingRequiredFields && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('REQUIRED_FIELDS_MISSING_ERROR')}
              </AlertDescription>
            </Alert>
          )}
          {!isCustomerTypeValid && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('INVALID_CUSTOMER_TYPE_SELECTED_ERROR')}
              </AlertDescription>
            </Alert>
          )}
          {!areCustomersTheSameType && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('MORE_THAN_ONE_CUSTOMER_TYPE_SELECTED_ERROR')}
              </AlertDescription>
            </Alert>
          )}
          {!areCustomersNaiveToMarket && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('CUSTOMERS_HAVE_EXISTING_ACCESSORIAL_RATES_ERROR')}
              </AlertDescription>
            </Alert>
          )}
          {duplicateCustomers && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('DUPLICATE_CUSTOMER_ERROR')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(true)}>
            {startCase(t('CANCEL'))}
          </Button>
          <Button
            disabled={saveDisabled || isFetchingExistingCustomers}
            onClick={handleAddRates}
          >
            {startCase(t('NEXT'))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
